import UnitDetailCard from '@AMIEWEB/Order/OrderCreationForm/Common/UnitDetailCard';
import { Grid, Skeleton, Typography } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SkillsProcedureSubSection } from './SkillsProcedureSubSection';
import ClinicianToPatientRatio, {
  IClinicianToPatientRatioProps,
} from '@AMIEWEB/Order/OrderCreationForm/Components/UnitDetail/AdditionalInformation/ClinicianToPatientRatio';
import { useDispatch, useSelector } from 'react-redux';
import { orderCreationNewActions } from '@AMIEWEB/NewOrder/Store/OrderCreation.actions';
import { orderCreationNewReducer, orderCreationNewSliceKey } from '@AMIEWEB/NewOrder/Store/OrderCreation.redux';
import { orderCreationNewSaga } from '@AMIEWEB/NewOrder/Store/OrderCreation.saga';
import { useInjectReducer, useInjectSaga } from 'redux-injectors';
import { selectClinicianToPatientRatio, selectPage2DefaultValues } from '@AMIEWEB/NewOrder/Store/Order.selectors';
import { WorkHistoryContainer } from './WorkHistory';
import VirtualRatingsOrderPriorities from './VirtualRatingsOrderPriorities';
import { VirtualInterviewOrOrderPriorities } from '@AMIEWEB/NewOrder/types';
import { BestBets } from './BestBets';
import { ReferencesContainer } from './References';
import { StrikeContainer } from './Strike';
import { selectedOrderTypeDetails as selectPage1 } from '@AMIEWEB/NewOrder/Store/Order.selectors';
import { usePromiseTracker } from 'react-promise-tracker';

export const AdditionalRequirements = () => {
  useInjectReducer({ key: orderCreationNewSliceKey, reducer: orderCreationNewReducer });
  useInjectSaga({ key: orderCreationNewSliceKey, saga: orderCreationNewSaga });
  const { promiseInProgress: fetchingOrderPriority } = usePromiseTracker({ area: 'order-priority-', delay: 0 });
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const arePage2DefaultValuesSet = useSelector(selectPage2DefaultValues);
  const clinicianPatientRatio = useSelector(selectClinicianToPatientRatio);
  const [keyClinicianToPatientRatio, setKeyClinicianToPatientRatio] = React.useState('');
  const page1 = useSelector(selectPage1);

  useEffect(() => {
    if (!arePage2DefaultValuesSet) dispatch(orderCreationNewActions.setDefaultValuesPage2());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getNameValuePairs = () => {
    const nameValuePairs: IClinicianToPatientRatioProps = {
      notifyCandidate: {
        name: 'orderClinicianToPatientRatio.notifyCandidate',
        value: clinicianPatientRatio?.notifyCandidate !== null ? clinicianPatientRatio?.notifyCandidate : null,
      },
      numberOfClinicians: {
        name: 'orderClinicianToPatientRatio.numberOfClinicians',
        value: clinicianPatientRatio?.numberOfClinicians !== null ? clinicianPatientRatio?.numberOfClinicians : null,
      },
      numberOfPatients: {
        name: 'orderClinicianToPatientRatio.numberOfPatients',
        value: clinicianPatientRatio?.numberOfPatients !== null ? `${clinicianPatientRatio?.numberOfPatients}` : null,
      },
      numberOfVisits: {
        name: 'orderClinicianToPatientRatio.numberOfVisits',
        value: clinicianPatientRatio?.numberOfVisits !== null ? `${clinicianPatientRatio?.numberOfVisits}` : null,
      },
      patientsSeen: {
        name: 'orderClinicianToPatientRatio.patientsSeen',
        value: clinicianPatientRatio?.patientsSeen !== null ? `${clinicianPatientRatio?.patientsSeen}` : null,
      },
      productivityPercentage: {
        name: 'orderClinicianToPatientRatio.productivityPercentage',
        value:
          clinicianPatientRatio?.productivityPercentage !== null ? clinicianPatientRatio?.productivityPercentage : null,
      },
    };
    setKeyClinicianToPatientRatio(JSON.stringify(clinicianPatientRatio));
    return { ...nameValuePairs };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  const clinicianPatient = useMemo(() => {
    const nvPair = { ...getNameValuePairs() };
    return <ClinicianToPatientRatio key={keyClinicianToPatientRatio} nameValuePairs={nvPair} />;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arePage2DefaultValuesSet, clinicianPatientRatio]);
  const strikeOrderType = 'Strike';

  const isDisable = page1?.orderType === strikeOrderType ? true : false;

  return (
    <Grid container spacing={4} sx={{ mt: '0px', pl: '11px', mb: '24px' }}>
      <Grid xs={12} sx={{ paddingLeft: '24px', marginTop: '10px' }}>
        <Typography
          variant="h6"
          sx={{
            font: 'normal normal normal 20px/26px Roboto',
            color: theme => theme.palette.system.darkGray,
            letterSpacing: '0px',
          }}
        >
          {t('placement.profile.tabs.additionalRequirements')}
        </Typography>
      </Grid>
      <Grid item xs={12} sx={{ paddingTop: '12px !important', display: 'none' }}>
        <UnitDetailCard
          title={t('order.newOrderCreation.tabs.skillAndProcedure')}
          children={<SkillsProcedureSubSection />}
          hasSubSection={true}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <UnitDetailCard
          title={t('order.newOrderCreation.tabs.strike')}
          isDisable={isDisable}
          children={<StrikeContainer />}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <UnitDetailCard title={t('order.newOrderCreation.tabs.clinicianToPatientRatio')} children={clinicianPatient} />
      </Grid>
      <Grid item xs={12} sm={6}>
        {fetchingOrderPriority ? (
          <UnitDetailCard
            title={t('order.newOrderCreation.tabs.orderPriorities')}
            children={<Skeleton height={50} width="100%" />}
          />
        ) : (
          <UnitDetailCard
            title={t('order.newOrderCreation.tabs.orderPriorities')}
            children={<VirtualRatingsOrderPriorities name={VirtualInterviewOrOrderPriorities?.name} />}
          />
        )}
      </Grid>
      <Grid item xs={12} sm={6}>
        {fetchingOrderPriority ? (
          <UnitDetailCard
            title={t('order.newOrderCreation.tabs.virtualInterviewRating')}
            children={<Skeleton height={50} width="100%" />}
          />
        ) : (
          <UnitDetailCard
            title={t('order.newOrderCreation.tabs.virtualInterviewRating')}
            children={<VirtualRatingsOrderPriorities {...VirtualInterviewOrOrderPriorities} />}
          />
        )}
      </Grid>
      <Grid item xs={12} sm={6}>
        <UnitDetailCard title={t('order.newOrderCreation.tabs.references')} children={<ReferencesContainer />} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <UnitDetailCard title={t('order.newOrderCreation.tabs.workHistory')} children={<WorkHistoryContainer />} />
      </Grid>
      <Grid item xs={12} sm={6} paddingBottom={6}>
        <UnitDetailCard
          title={t('order.newOrderCreation.tabs.bestBets')}
          hideRemoveSection={false}
          children={<BestBets />}
        />
      </Grid>
    </Grid>
  );
};
